import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TagChipList from './TagChipList';

function buildBreadcrumb(tag, tagsById) {
  const parts = [];
  let current = tag;
  const seen = new Set();
  while (current && current.parent_ids && current.parent_ids.length > 0 && !seen.has(current.id)) {
    seen.add(current.id);
    const parent = tagsById[current.parent_ids[0]];
    if (!parent) break;
    parts.unshift(parent.name);
    current = parent;
  }
  return parts.join(' / ');
}

// Attached tags are always shown with their edit controls (removable chips) rather than a
// separate read-only view alongside — there's only ever one place to look. Adding a tag opens a
// search dropdown over the page: the candidate list used to sit open underneath the field at all
// times, which turned every picker (and there is one per file in the document library) into a
// permanent wall of cards.
//
// The list is a plain divided list inside one bordered panel, not a stack of cards — candidates
// are rows of a single menu, and a card each made the panel read as several separate things.
//
// `pinGlobal` is for scope pickers (workflows, files), where Global is the one tag an admin
// reaches for by name rather than by searching: it gets a fixed toggle above the search, out of
// the candidate list, and out of the chips — its state is already visible in the toggle, so
// showing it twice would imply two separate things to turn off. Relation pickers on the tag
// tree leave it off, because there Global is an ordinary parent candidate and labelling it
// "applies to all assets" would describe asset matching rather than the edge being created.
//
// Callbacks come in two shapes. `onChange` receives the whole next selection and is what scope
// pickers use, because there a single gesture moves two things at once — naming a tag also
// takes Global off — and `onAdd` followed by `onRemove` would compute both from the same stale
// array, the second silently undoing the first. Relation pickers move one edge at a time and
// keep the narrower `onAdd`/`onRemove` pair.
export default function TagRelationPicker({
  tags,
  selectedIds,
  autoTagIds,
  excludeIds,
  onChange,
  onAdd,
  onRemove,
  placeholder,
  emptyHint,
  disabled,
  pinGlobal,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();
  const [panelStyle, setPanelStyle] = useState(null);

  const tagsById = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t])), [tags]);
  const globalTag = pinGlobal ? tags.find((t) => t.is_system) : undefined;
  const globalSelected = globalTag ? selectedIds.includes(globalTag.id) : false;

  const selectedTags = selectedIds
    .map((id) => tagsById[id])
    .filter(Boolean)
    .filter((t) => !(pinGlobal && t.is_system));

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tags
      .filter((t) => !(pinGlobal && t.is_system))
      .filter((t) => !selectedIds.includes(t.id))
      .filter((t) => !excludeIds.includes(t.id))
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .slice(0, 20);
  }, [tags, query, selectedIds, excludeIds, pinGlobal]);

  // Results shrink as tags are added and as the query narrows, so the stored index is only a
  // request — what is actually highlighted is clamped to the list that exists right now.
  const highlighted = results.length === 0 ? -1 : Math.min(activeIndex, results.length - 1);

  // The panel is rendered into document.body rather than next to the field. These pickers now
  // sit inside a table cell and inside side panels, and both of those clip their overflow — the
  // table wrapper has to, or its rounded corners would not clip the rows. Positioned from the
  // field's own rect, and flipped above it when the space below is too shallow to be useful.
  const positionPanel = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 16;
    const above = rect.top - 16;
    const flip = below < 160 && above > below;
    setPanelStyle({
      position: 'fixed',
      top: flip ? undefined : rect.bottom + 4,
      bottom: flip ? window.innerHeight - rect.top + 4 : undefined,
      left: rect.left,
      minWidth: Math.max(rect.width, 260),
      maxWidth: Math.min(360, window.innerWidth - rect.left - 16),
      maxHeight: Math.max(120, Math.min(240, flip ? above : below)),
      zIndex: 1056,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) positionPanel();
  }, [open, positionPanel, results.length]);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => positionPanel();
    // Capture, so a scroll inside any ancestor (a side panel, the page) moves the panel too.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, positionPanel]);

  // The field only exists while the picker is open, so focus has to follow it there.
  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQuery('');
  }, [open]);

  // Closing on outside mousedown rather than on input blur: blur fires before the click lands on
  // a candidate row, which would close the panel out from under the pointer.
  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (event) => {
      const inside = containerRef.current?.contains(event.target)
        || panelRef.current?.contains(event.target);
      if (!inside) setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  // Naming a tag is how an admin narrows a scope, so it also lifts Global: the two are
  // alternatives, and leaving both on would mean "all assets, and also these" — which is just
  // all assets with a decoration. Only possible via onChange, as one update.
  const applyAdd = useCallback((tagId) => {
    if (!onChange) {
      onAdd(tagId);
      return;
    }
    const kept = globalTag ? selectedIds.filter((id) => id !== globalTag.id) : selectedIds;
    onChange([...kept, tagId]);
  }, [onChange, onAdd, globalTag, selectedIds]);

  const applyRemove = useCallback((tagId) => {
    if (!onChange) {
      onRemove(tagId);
      return;
    }
    onChange(selectedIds.filter((id) => id !== tagId));
  }, [onChange, onRemove, selectedIds]);

  // And the mirror: turning Global on drops the tags rather than parking them. Keeping them
  // attached-but-ignored meant the picker held a scope nobody could see and had to be told
  // about in words ("2 tags ignored while Global is on").
  const applyGlobalToggle = useCallback(() => {
    if (!globalTag) return;
    if (globalSelected) {
      applyRemove(globalTag.id);
      return;
    }
    if (!onChange) {
      onAdd(globalTag.id);
      return;
    }
    onChange([globalTag.id]);
  }, [globalTag, globalSelected, applyRemove, onChange, onAdd]);

  const handleSelect = useCallback((tag) => {
    if (disabled) return;
    applyAdd(tag.id);
    // The picked tag leaves the list, so a kept query would usually leave it empty. Clearing
    // shows the full list again, ready for the next pick — the panel stays open for it.
    setQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  }, [disabled, applyAdd]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) { setOpen(true); return; }
      setActiveIndex(Math.min(highlighted + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(Math.max(highlighted - 1, 0));
    } else if (event.key === 'Enter') {
      // Any Enter with the panel open belongs to the picker, match or not — these pickers sit
      // inside forms (new workflow), where it would otherwise submit the whole form.
      if (open) {
        event.preventDefault();
        if (highlighted >= 0) handleSelect(results[highlighted]);
      }
    } else if (event.key === 'Escape') {
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  // `pinGlobal` pickers scope a thing to assets (a document, a workflow's reach). There, Global
  // and a list of tags are alternatives rather than additions: once it applies to all assets,
  // naming some of them says nothing. Asset tagging has no such rule — an asset's tags describe
  // it — which is why this hangs off pinGlobal and not off the presence of a Global tag.
  //
  // The add control stays available while Global is on, because that is how the scope gets
  // narrowed: picking the first tag is the gesture that replaces "everything" with "these".
  const scopedToEverything = globalSelected;

  const addLabel = placeholder.replace(/[….]+$/, '');

  return (
    <div ref={containerRef} className="position-relative">
      <div className="d-flex flex-wrap align-items-center gap-1">
        {/* A control, so it is shaped like one: an outlined toggle that fills in when pressed.
            It used to be a <button> wearing a pill's clothes, sitting among chips that do not
            toggle — the one genuinely confusable thing in the tag UI. */}
        {globalTag && (
          <button
            type="button"
            className={`btn btn-sm rounded-pill py-0 px-3 ${globalSelected ? 'btn-primary' : 'btn-outline-primary'}`}
            disabled={disabled}
            title="Global — applies to every asset"
            aria-pressed={globalSelected}
            onClick={applyGlobalToggle}
          >
            {globalSelected && <i className="bi bi-check-lg me-1" aria-hidden />}
            Global
          </button>
        )}

        {scopedToEverything ? (
          <span className="text-muted small ms-1">All assets</span>
        ) : (
          <>
            <TagChipList
              tags={selectedTags}
              autoTagIds={autoTagIds}
              removable
              onRemove={(tag) => applyRemove(tag.id)}
            />
            {selectedTags.length === 0 && emptyHint && (
              <span className="text-muted small me-1">{emptyHint}</span>
            )}
          </>
        )}

        {/* Closed, this is a chip-sized button; open, it becomes the search field in the
            same spot, so the row never jumps and the dropdown stays where the eye is. */}
        <span ref={anchorRef} className="d-inline-flex">
        {open && !disabled ? (
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            className="mntl-tag-input"
            placeholder={placeholder}
            value={query}
            autoComplete="off"
            aria-label={placeholder}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && highlighted >= 0 ? `${listId}-${results[highlighted].id}` : undefined}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <button
            type="button"
            className="mntl-tag-add"
            disabled={disabled}
            aria-expanded={false}
            onClick={() => { setOpen(true); setActiveIndex(0); }}
          >
            <i className="bi bi-plus-lg" aria-hidden />
            {addLabel}
          </button>
        )}
        </span>
      </div>

      {open && !disabled && panelStyle && createPortal(
        <div
          ref={panelRef}
          id={listId}
          role="listbox"
          aria-label={placeholder}
          className="bg-white border rounded shadow-sm overflow-auto"
          style={panelStyle}
        >
          {results.length === 0 ? (
            <p className="text-muted small mb-0 px-3 py-2">No matching tags</p>
          ) : (
            results.map((tag, index) => {
              const breadcrumb = buildBreadcrumb(tag, tagsById) || 'root';
              return (
                <div
                  key={tag.id}
                  id={`${listId}-${tag.id}`}
                  role="option"
                  aria-selected={index === highlighted}
                  tabIndex={-1}
                  className={classNames(
                    'd-flex justify-content-between align-items-center gap-2 px-3 py-2',
                    index > 0 && 'border-top',
                    index === highlighted && 'bg-light',
                  )}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handleSelect(tag)}
                >
                  <span className="small fw-semibold text-truncate">{tag.name}</span>
                  <span className="text-muted small text-truncate">{breadcrumb}</span>
                </div>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

TagRelationPicker.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
    is_system: PropTypes.bool,
    parent_ids: PropTypes.array,
  })),
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  autoTagIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  excludeIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  // Either onChange (scope pickers, atomic) or the onAdd/onRemove pair (relation pickers).
  onChange: PropTypes.func,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  placeholder: PropTypes.string,
  emptyHint: PropTypes.string,
  disabled: PropTypes.bool,
  pinGlobal: PropTypes.bool,
};

TagRelationPicker.defaultProps = {
  tags: [],
  selectedIds: [],
  autoTagIds: [],
  excludeIds: [],
  onChange: undefined,
  onAdd: undefined,
  onRemove: undefined,
  placeholder: 'Add a tag…',
  emptyHint: undefined,
  disabled: false,
  pinGlobal: false,
};
